import { useState, useEffect } from "react";
import { Link, Navigate } from "react-router";
import { useAuth } from "../../hooks/useAuth";
import { getMyBusinesses } from "../../api/business.api";
import { updateAccountType } from "../../api/auth.api";
import { useAuthStore } from "../../stores/auth.store";
import { CATEGORY_LABELS } from "@abejon/shared";
import { Skeleton, SkeletonText, SkeletonCircle } from "../../components/ui/Skeleton";
import type { BusinessProfile } from "@abejon/shared";
import { PasskeyManager } from "../../components/PasskeyManager";

export function AccountPage() {
  const { user, isLoading, isAuthenticated, logout } = useAuth();
  const setUser = useAuthStore((s) => s.setUser);
  const [businesses, setBusinesses] = useState<BusinessProfile[]>([]);
  const [loadingBusinesses, setLoadingBusinesses] = useState(true);
  const [switching, setSwitching] = useState(false);

  useEffect(() => {
    if (!user) return;
    let cancelled = false;

    async function load() {
      try {
        const data = await getMyBusinesses();
        if (!cancelled) setBusinesses(data.businesses);
      } catch {
        // User may not have any businesses
      } finally {
        if (!cancelled) setLoadingBusinesses(false);
      }
    }
    load();
    return () => { cancelled = true; };
  }, [user]);

  if (isLoading) {
    return (
      <div className="mx-auto max-w-lg py-8 px-4">
        {/* User info */}
        <div className="mb-8 flex items-center gap-4">
          <SkeletonCircle className="h-14 w-14" />
          <div className="space-y-2">
            <SkeletonText className="h-5 w-36" />
            <SkeletonText className="h-4 w-48" />
          </div>
        </div>
        {/* Business pages section */}
        <SkeletonText className="mb-3 h-5 w-40" />
        <Skeleton className="mb-8 h-20 w-full rounded-lg" />
        {/* Bookings section */}
        <SkeletonText className="mb-3 h-5 w-28" />
        <Skeleton className="mb-8 h-16 w-full rounded-lg" />
        {/* Actions */}
        <Skeleton className="h-10 w-full rounded-lg" />
      </div>
    );
  }

  if (!isAuthenticated) {
    return <Navigate to="/login" replace />;
  }

  return (
    <div className="mx-auto max-w-lg py-8 px-4">
      {/* User Info */}
      <div className="mb-8 flex items-center gap-4">
        <div className="flex h-14 w-14 items-center justify-center rounded-full bg-gray-900 text-xl font-semibold text-white">
          {user!.name.charAt(0).toUpperCase()}
        </div>
        <div>
          <h1 className="text-xl font-bold">{user!.name}</h1>
          <p className="text-sm text-gray-500">{user!.email}</p>
        </div>
      </div>

      {/* Business Pages */}
      <section className="mb-8">
        <h2 className="mb-3 text-lg font-semibold">My Business Pages</h2>
        {loadingBusinesses ? (
          <div className="space-y-3">
            <Skeleton className="h-20 w-full rounded-lg" />
          </div>
        ) : businesses.length > 0 ? (
          <div className="space-y-3">
            {businesses.map((biz) => (
              <Link
                key={biz.id}
                to={`/b/${biz.slug}`}
                className="block rounded-lg border border-gray-200 p-4 hover:border-gray-400"
              >
                <div className="font-medium">{biz.displayName}</div>
                <div className="mt-1 flex items-center gap-2 text-sm text-gray-500">
                  <span>{CATEGORY_LABELS[biz.category]}</span>
                  {biz.city && biz.state && (
                    <>
                      <span>&middot;</span>
                      <span>{biz.city}, {biz.state}</span>
                    </>
                  )}
                </div>
                <div className="mt-1 text-xs text-gray-400">/b/{biz.slug}</div>
              </Link>
            ))}
          </div>
        ) : (
          <div className="rounded-lg border border-dashed border-gray-300 p-6 text-center">
            <p className="text-sm text-gray-500">No business pages yet.</p>
            {user!.accountType === "business" && (
              <Link
                to="/dashboard/settings"
                className="mt-2 inline-block text-sm font-medium text-gray-900 underline"
              >
                Create one
              </Link>
            )}
          </div>
        )}
      </section>

      {/* Bookings */}
      <section className="mb-8">
        <h2 className="mb-3 text-lg font-semibold">My Bookings</h2>
        <Link
          to="/my-bookings"
          className="block rounded-lg border border-gray-200 p-4 text-center hover:border-gray-400"
        >
          <span className="text-sm font-medium text-gray-900">View all bookings</span>
          <p className="mt-1 text-xs text-gray-500">See upcoming and past appointments</p>
        </Link>
      </section>

      {/* Business Dashboard */}
      {user!.accountType === "business" && (
        <section className="mb-8">
          <h2 className="mb-3 text-lg font-semibold">Dashboard</h2>
          <div className="grid grid-cols-2 gap-3">
            <Link
              to="/dashboard/bookings"
              className="rounded-lg border border-gray-200 p-4 text-center hover:border-gray-400"
            >
              <span className="text-sm font-medium text-gray-900">Bookings</span>
              <p className="mt-0.5 text-xs text-gray-500">Today's schedule</p>
            </Link>
            <Link
              to="/dashboard/services"
              className="rounded-lg border border-gray-200 p-4 text-center hover:border-gray-400"
            >
              <span className="text-sm font-medium text-gray-900">Services</span>
              <p className="mt-0.5 text-xs text-gray-500">Manage menu</p>
            </Link>
            <Link
              to="/dashboard/posts"
              className="rounded-lg border border-gray-200 p-4 text-center hover:border-gray-400"
            >
              <span className="text-sm font-medium text-gray-900">Posts</span>
              <p className="mt-0.5 text-xs text-gray-500">Share your work</p>
            </Link>
            <Link
              to="/dashboard/schedule"
              className="rounded-lg border border-gray-200 p-4 text-center hover:border-gray-400"
            >
              <span className="text-sm font-medium text-gray-900">Schedule</span>
              <p className="mt-0.5 text-xs text-gray-500">Hours & availability</p>
            </Link>
            <Link
              to="/dashboard/settings"
              className="rounded-lg border border-gray-200 p-4 text-center hover:border-gray-400"
            >
              <span className="text-sm font-medium text-gray-900">Settings</span>
              <p className="mt-0.5 text-xs text-gray-500">Profile & info</p>
            </Link>
          </div>
        </section>
      )}

      {/* Passkeys */}
      <PasskeyManager />

      {/* Actions */}
      <div className="space-y-3">
        {user!.accountType === "user" && (
          <button
            onClick={async () => {
              setSwitching(true);
              try {
                const { user: updated } = await updateAccountType("business");
                setUser(updated);
              } catch {
                // ignore
              } finally {
                setSwitching(false);
              }
            }}
            disabled={switching}
            className="w-full rounded-lg border border-gray-300 px-4 py-2.5 text-sm font-medium text-gray-700 hover:bg-gray-50 disabled:opacity-50"
          >
            {switching ? "Switching..." : "Switch to Business Account"}
          </button>
        )}
        <button
          onClick={logout}
          className="w-full rounded-lg border border-red-200 px-4 py-2.5 text-sm font-medium text-red-600 hover:bg-red-50"
        >
          Log out
        </button>
      </div>
    </div>
  );
}
